import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FaWhatsapp } from 'react-icons/fa';
import api from '../../services/api';
import { burkinaPhoneRule, BURKINA_PHONE_HINT } from '../../utils/phoneUtils';
import Select from 'react-select';
import ProfessionModal from './ProfessionModal';
import ReligionModal from './ReligionModal';

const WhatsappToggle = ({ active, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    title={active ? 'Numéro WhatsApp' : 'Marquer comme WhatsApp'}
    className={`flex-shrink-0 p-2.5 rounded-lg border-2 transition-all duration-150 ${
      active
        ? 'bg-[#25D366] border-[#25D366] text-white shadow-sm'
        : 'bg-[#25D366]/10 border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/20 hover:border-[#25D366]'
    }`}
  >
    <FaWhatsapp className="h-4 w-4" />
  </button>
);

const ParentModal = ({ onClose, onSuccess, parent = null }) => {
  const isEdit = Boolean(parent?.id);
  const { register, handleSubmit, setValue, watch, reset, trigger, formState: { errors } } = useForm({
    defaultValues: { telmob1_whatsapp: false, telmob2_whatsapp: false },
  });
  const [typesParentes, setTypesParentes] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [religions, setReligions] = useState([]);
  const [showProfessionModal, setShowProfessionModal] = useState(false);
  const [showReligionModal, setShowReligionModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const [typesRes, profsRes, relsRes] = await Promise.all([
        api.get('/types_parentes'),
        api.get('/professions'),
        api.get('/religions')
      ]);
      setTypesParentes(typesRes.data.data || []);
      setProfessions(profsRes.data.data || []);
      setReligions(relsRes.data.data || []);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!parent) {
      reset({ telmob1_whatsapp: false, telmob2_whatsapp: false });
      return;
    }
    reset({
      type_parente_id: parent.type_parente_id ? Number(parent.type_parente_id) : '',
      profession_id: parent.profession_id ? Number(parent.profession_id) : '',
      religion_id: parent.religion_id ? Number(parent.religion_id) : '',
      nom: parent.nom || '',
      prenom: parent.prenom || '',
      email: parent.email || '',
      telmob1: parent.telmob1 || '',
      telmob2: parent.telmob2 || '',
      telmob1_whatsapp: parent.telmob1_whatsapp === 1 || parent.telmob1_whatsapp === '1' || parent.telmob1_whatsapp === true,
      telmob2_whatsapp: parent.telmob2_whatsapp === 1 || parent.telmob2_whatsapp === '1' || parent.telmob2_whatsapp === true,
      adresse: parent.adresse || '',
    });
  }, [parent, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        email: (data.email || '').trim(),
        telmob1: (data.telmob1 || '').trim() || null,
        telmob2: (data.telmob2 || '').trim() || null,
        telmob1_whatsapp: data.telmob1_whatsapp ? 1 : 0,
        telmob2_whatsapp: data.telmob2_whatsapp ? 1 : 0,
      };
      if (isEdit) {
        await api.put(`/parents/${parent.id}`, payload);
      } else {
        await api.post('/parents', payload);
      }
      onSuccess(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-2xl">
        <h2 className="text-xl font-semibold mb-4">{isEdit ? 'Modifier le parent' : 'Ajouter un parent'}</h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <Select
                options={typesParentes.map(tp => ({ value: tp.id, label: tp.libelle }))}
                value={typesParentes.find(tp => Number(tp.id) === Number(watch('type_parente_id'))) ? { value: watch('type_parente_id'), label: typesParentes.find(tp => Number(tp.id) === Number(watch('type_parente_id'))).libelle } : null}
                onChange={opt => setValue('type_parente_id', opt ? opt.value : '')}
                isClearable
                placeholder="Sélectionner le type"
                classNamePrefix="react-select"
              />
              {errors.type_parente_id && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Profession</label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Select
                    options={professions.map(p => ({ value: p.id, label: p.profession }))}
                    value={professions.find(p => Number(p.id) === Number(watch('profession_id'))) ? { value: watch('profession_id'), label: professions.find(p => Number(p.id) === Number(watch('profession_id'))).profession } : null}
                    onChange={opt => setValue('profession_id', opt ? opt.value : '')}
                    isClearable
                    placeholder="Sélectionner une profession"
                    classNamePrefix="react-select"
                  />
                </div>
                <button type="button" onClick={() => setShowProfessionModal(true)} className="px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700">+</button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input {...register('nom', { required: true })} className="block w-full rounded-lg border border-gray-300 px-3 py-2" />
              {errors.nom && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
              <input {...register('prenom', { required: true })} className="block w-full rounded-lg border border-gray-300 px-3 py-2" />
              {errors.prenom && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Religion</label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Select
                    options={religions.map(r => ({ value: r.id, label: r.religion }))}
                    value={religions.find(r => Number(r.id) === Number(watch('religion_id'))) ? { value: watch('religion_id'), label: religions.find(r => Number(r.id) === Number(watch('religion_id'))).religion } : null}
                    onChange={opt => setValue('religion_id', opt ? opt.value : '')}
                    isClearable
                    placeholder="Sélectionner une religion"
                    classNamePrefix="react-select"
                  />
                </div>
                <button type="button" onClick={() => setShowReligionModal(true)} className="px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700">+</button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                {...register('email', {
                  required: 'Email obligatoire',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Email invalide' },
                })}
                className="block w-full rounded-lg border border-gray-300 px-3 py-2"
              />
              {errors.email && <span className="text-red-500 text-xs mt-1">{errors.email.message}</span>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone 1 *</label>
              <div className="flex gap-2 items-center">
                <input
                  {...register('telmob1', {
                    validate: (value) => {
                      const t1 = (value || '').trim();
                      const t2 = (watch('telmob2') || '').trim();
                      if (!t1 && !t2) return 'Au moins un téléphone requis';
                      if (t1) {
                        const fmt = burkinaPhoneRule.validate(t1);
                        if (fmt !== true) return fmt;
                      }
                      return true;
                    },
                  })}
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder={`Ex: ${BURKINA_PHONE_HINT}`}
                />
                <WhatsappToggle
                  active={!!watch('telmob1_whatsapp')}
                  onToggle={() => setValue('telmob1_whatsapp', !watch('telmob1_whatsapp'), { shouldDirty: true })}
                />
              </div>
              {errors.telmob1 && <span className="text-red-500 text-xs mt-1">{errors.telmob1.message}</span>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone 2</label>
              <div className="flex gap-2 items-center">
                <input
                  {...register('telmob2', {
                    validate: (value) => {
                      const v = (value || '').trim();
                      if (!v) return true;
                      return burkinaPhoneRule.validate(v);
                    },
                    onChange: () => trigger('telmob1'),
                  })}
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder={`Ex: ${BURKINA_PHONE_HINT}`}
                />
                <WhatsappToggle
                  active={!!watch('telmob2_whatsapp')}
                  onToggle={() => setValue('telmob2_whatsapp', !watch('telmob2_whatsapp'), { shouldDirty: true })}
                />
              </div>
              {errors.telmob2 && <span className="text-red-500 text-xs mt-1">{errors.telmob2.message}</span>}
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
              <input {...register('adresse')} className="block w-full rounded-lg border border-gray-300 px-3 py-2" />
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-6">
            <button type="button" className="px-4 py-2 bg-gray-200 rounded-lg" onClick={onClose}>Annuler</button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
        {showProfessionModal && (
          <ProfessionModal
            onClose={() => setShowProfessionModal(false)}
            onSuccess={async (newProf) => {
              setShowProfessionModal(false);
              const res = await api.get('/professions');
              setProfessions(res.data.data || []);
              if (newProf && newProf.id) {
                setValue('profession_id', newProf.id);
              }
            }}
          />
        )}
        {showReligionModal && (
          <ReligionModal
            onClose={() => setShowReligionModal(false)}
            onSuccess={async (newRel) => {
              setShowReligionModal(false);
              const res = await api.get('/religions');
              setReligions(res.data.data || []);
              if (newRel && newRel.id) {
                setValue('religion_id', newRel.id);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ParentModal;